import { useRef } from 'react';
import PropTypes from 'prop-types';
import Carousel from '@comp/Carousel/Carousel';
import TestSeriesCard from '@comp/TestSeriesCard/TestSeriesCard';
import { css } from '@emotion/react';
import { breakpoints } from '@styles/index';
import { segment } from '@actions/analytics';
import {
  SYLLABUS_SEE_ALL_CLICKED,
  GTP_SEE_ALL_CLICKED
} from '@constants/segment';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import GtpTestSeriesCard from '@comp/TestSeriesCard/GtpTestSeriesCard';
import useSubscribed from '@hooks/useSubscribed';
import { getIsGTPGoal } from '@api/selectors/constants';
import { useIsMobile } from '@stores/AppCommonData';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useConstants from '@api/hooks/useConstants';

const carouselCss = css`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    & div {
      padding: 0;
      margin: 0;
      grid-template-columns: repeat(10, 1fr);
      grid-row-gap: 16px;
      div {
        grid-column: span 10;
      }
    }
  }
`;

const TestSeriesFeedBlock = ({
  goalSlug,
  goalUID,
  testSeriesFeedBlock,
  feedBlocktype = 'plus',
  lastPrimarySource,
  dataAnalytics
}) => {
  const isMobile = useIsMobile();
  const viewRef = useRef();
  const count = testSeriesFeedBlock?.extraBlockinfo?.count;
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const minCountForSeeAll = isGTP ? 0 : 3;
  let seeAllLink =
    count > minCountForSeeAll
      ? `/goal/${goalSlug}/${goalUID}/test-series`
      : null;
  let href = '/goal/[goalSlug]/[goalUID]/test-series';
  const { label } = testSeriesFeedBlock;
  if (feedBlocktype === 'special') {
    seeAllLink = count > 3 ? `/goal/${goalSlug}/${goalUID}/test-series` : null;
    href = `/goal/[goalSlug]/[goalUID]/${'test-series'}`;
  }
  const isSubscribed = useSubscribed(goalUID);
  const goalSegmentData = useGoalEventProps({ goalUID });

  const tests = isGTP ? testSeriesFeedBlock.data : testSeriesFeedBlock.courses;

  const seeAllClickEvent = () => {
    if (isGTP) {
      segment.track(GTP_SEE_ALL_CLICKED, {
        ...goalSegmentData,
        last_primary_source: lastPrimarySource,
        last_primary_source_section: 'Tests see all'
      });
    } else {
      segment.track(SYLLABUS_SEE_ALL_CLICKED, {
        expansion_type: testSeriesFeedBlock.label,
        goal_name: goalSlug,
        goal_uid: goalUID,
        is_subscription_active: hasActiveSubscriptionToGoal(
          { uid: goalUID },
          goalUID
        ),
        subscription_duration: 'NA',
        subscription_end_date: 'NA',
        last_primary_source: lastPrimarySource,
        last_primary_source_section: testSeriesFeedBlock.label
      });
    }
  };

  return (
    <>
      <Carousel
        label={label}
        seeAllLink={seeAllLink}
        href={href}
        dataAnalytics="goal-testSeries"
        ref={viewRef}
        $css={carouselCss}
        itemGridColumn={isGTP ? 'span 9' : 'span 6'}
        handleClickSeeAll={seeAllClickEvent}
      >
        {tests.map((test, _index) => {
          if (isMobile && _index > 1 && seeAllLink) return null;
          return isGTP ? (
            <GtpTestSeriesCard
              test={test}
              isSubscribed={isSubscribed}
              key={test.uid}
            />
          ) : (
            <TestSeriesCard
              label={label}
              key={test.uid}
              test={test}
              index={_index}
              lastPrimarySource={lastPrimarySource}
              dataAnalytics={dataAnalytics}
            />
          );
        })}
      </Carousel>
    </>
  );
};

export default TestSeriesFeedBlock;

TestSeriesFeedBlock.propTypes = {
  goalSlug: PropTypes.string.isRequired,
  goalUID: PropTypes.string.isRequired,
  testSeriesFeedBlock: PropTypes.objectOf(PropTypes.any).isRequired,
  feedBlocktype: PropTypes.string,
  dataAnalytics: PropTypes.string,
  lastPrimarySource: PropTypes.string
};

TestSeriesFeedBlock.defaultProps = {
  feedBlocktype: 'plus',
  dataAnalytics: '',
  lastPrimarySource: ''
};
